import {last} from 'lodash';
import reduce from 'lodash/reduce';
import {getCMSItemFromStore} from '@packages/systems/cms/client-selectors';
import CollectionRecord from '@packages/systems/dynamo/records/CollectionRecord';
import Field from '@packages/systems/dynamo/records/Field';

import {singleMemo, weakMemo} from '@packages/utilities/memo';
import type {CollectionRecordType} from '@packages/systems/dynamo/records/CollectionRecord';
import type {
  CollectionsType,
  ItemsType,
  CollectionStateType,
  ItemCountByCollectionIdType,
} from '@packages/systems/core/records/CollectionStateType';
import type {FieldType} from '@packages/systems/dynamo/records/Field';
import {
  COMMERCE_CATEGORY_COLLECTION_SLUG,
  COMMERCE_PLUGIN_KEY,
  COMMERCE_PRODUCT_COLLECTION_SLUG,
  COMMERCE_PRODUCT_FIELD_SLUG,
  COMMERCE_SKU_COLLECTION_SLUG,
  COMMERCE_SKU_FIELD_SLUG,
  DEFAULT_SKU_SLUG,
  DEFAULT_TAX_CATEGORY,
  DEFAULT_PRODUCT_TYPE_ID,
  INFINITE_INVENTORY,
  INVENTORY_TYPE_FINITE,
  INVENTORY_TYPE_INFINITE,
} from '@packages/systems/commerce/constants';
import {COLLECTION_TYPES} from '@packages/systems/dynamo/constants';
import {
  DEFAULT_DATETIME_FIELD_SLUGS,
  DEFAULT_FIELD_SLUGS_FOR_PRODUCT_COLLECTION_VARIANTS,
  HARD_CODED_FIELDS_PER_COLLECTION_TYPE,
  getDefaultFieldSlugs,
} from '@packages/systems/dynamo/utils/CollectionFieldUtils';
import moment from 'moment';
import negate from 'lodash/negate';
import {
  List,
  Map,
  Record as ImRecord,
  type List as ListType,
  type Map as MapType,
  type RecordOf,
} from 'immutable';
import {isMintId} from '@packages/systems/core/utils/RecordUtils';
import {pick} from '@packages/systems/core/utils/ImmUtils';
import {get, getIn} from '@packages/utilities/imm4-backports';
import {reportBug} from '@packages/utilities/reporter';
import {type Order} from '@packages/systems/commerce/orderManagement';
import {
  ecommExtraFields,
  commerceCountryList,
} from '@packages/systems/core/constants/SharedConfig';

import {
  type ValidShippingZoneType,
  type CommerceStateShape,
  type UnitSystemType,
  type ShippingMethodType,
  type ValidCurrencyFormatType,
  type PaymentGatewaySupportType,
  type ValidCurrencyLocaleType,
  type CommerceSettingsStateRecordShape,
  type GettingStartedStatusStateShape,
  GETTING_STARTED_TO_DO_STATUSES,
  NETWORK_STATUSES,
  CLIENT_ORDER_STATES,
} from '@packages/systems/commerce/store';
import {emptyArray} from '@packages/utilities/fp/utils';
import {
  getVariantName as baseGetVariantName,
  getVariantSlug as baseGetVariantSlug,
  optionCombinationsFromOptionSets as baseOptionCombinationsFromOptionSets,
} from '@packages/shared/utils/SharedCommerceUtils';
import type {PageRecordType} from '@packages/systems/core/records/PageRecord';
import type {CommerceSettingsPanelSettingType} from '@packages/systems/designer/settings';
import {isFileUploading} from '@packages/shared/utils/AssetUtils';
import type {
  Variant,
  OptionCombination,
  Inventory,
  DiscountStatus,
  OptionCombinationHash,
  VariantsByOptionCombinationHash,
  SkuPropertiesRecordType,
  Discount,
  VariantValue,
} from '@packages/systems/commerce/core';
import type {ImageRefType} from '@packages/systems/core/records/ImageRefRecord';
import type {Locale} from '@packages/systems/localization-types';
import {reifyField} from '@packages/systems/core/render';
import {s3UrlToCdn} from '@packages/systems/core/utils/SrcsetUtils';
import {ItemRecordType} from '@packages/systems/core/records/ItemRecord';

const {CATEGORIES, PRODUCTS} = COLLECTION_TYPES;

const NumberFieldValidation = ImRecord(
  {
    precision: null,
    format: null,
  },
  'NumberFieldValidation'
);

const ItemRefFieldValidation = ImRecord(
  {
    collectionId: null,
  },
  'ItemRefFieldValidation'
);

// These ids will be used to generate an in-memory collection
// for the orders at render time, with the purpose of reusing
// as much rendering logic as possible from Dynamo.
export const COMMERCE_ORDER_ROOT_COLLECTION_ID =
  'COMMERCE_ORDER_ROOT_COLLECTION_ID';
export const COMMERCE_ORDER_USER_ITEMS_COLLECTION_ID =
  'COMMERCE_ORDER_USER_ITEMS_COLLECTION_ID';
export const COMMERCE_ORDER_SHIPPING_METHODS_COLLECTION_ID =
  'COMMERCE_ORDER_SHIPPING_METHODS_COLLECTION_ID';
export const COMMERCE_ORDER_EXTRA_ITEMS_COLLECTION_ID =
  'COMMERCE_ORDER_EXTRA_ITEMS_COLLECTION_ID';
export const COMMERCE_CART_COLLECTION_ID = 'COMMERCE_CART_COLLECTION_ID';
export const COMMERCE_PRODUCT_OPTIONS_COLLECTION_ID =
  'COMMERCE_PRODUCT_OPTIONS_COLLECTION_ID';
export const COMMERCE_DOWNLOAD_FILES_COLLECTION_ID =
  'COMMERCE_DOWNLOAD_FILES_COLLECTION_ID';
export const COMMERCE_ADD_TO_CART_PRODUCT_OPTIONS_COLLECTION_ID =
  'COMMERCE_ADD_TO_CART_PRODUCT_OPTIONS_COLLECTION_ID';

export const COMMERCE_ORDER_ROOT_COLLECTION = CollectionRecord({
  _id: COMMERCE_ORDER_ROOT_COLLECTION_ID,
  slug: 'order',
  name: 'Order',
  fields: List.of(
    Field({name: 'Total', slug: 'total', type: 'CommercePrice'}),
    Field({name: 'Subtotal', slug: 'subtotal', type: 'CommercePrice'}),
    Field({name: 'Customer Email', slug: 'customerEmail', type: 'PlainText'}),
    Field({name: 'Card Provider', slug: 'cardProvider', type: 'PlainText'}),
    Field({name: 'Card Last Four', slug: 'cardLastFour', type: 'PlainText'}),
    Field({
      name: 'Card Expires Month',
      slug: 'cardExpiresMonth',
      type: 'PlainText',
    }),
    Field({
      name: 'Card Expires Year',
      slug: 'cardExpiresYear',
      type: 'PlainText',
    }),
    Field({
      name: 'Shipping Address Addressee',
      slug: 'shippingAddressAddressee',
      type: 'PlainText',
    }),
    Field({
      name: 'Shipping Address Line 1',
      slug: 'shippingAddressLine1',
      type: 'PlainText',
    }),
    Field({
      name: 'Shipping Address Line 2',
      slug: 'shippingAddressLine2',
      type: 'PlainText',
    }),
    Field({
      name: 'Shipping Address City',
      slug: 'shippingAddressCity',
      type: 'PlainText',
    }),
    Field({
      name: 'Shipping Address State',
      slug: 'shippingAddressState',
      type: 'PlainText',
    }),
    Field({
      name: 'Shipping Address Postal Code',
      slug: 'shippingAddressPostalCode',
      type: 'PlainText',
    }),
    Field({
      name: 'Shipping Address Country',
      slug: 'shippingAddressCountry',
      type: 'PlainText',
    }),
    Field({
      name: 'Billing Address Addressee',
      slug: 'billingAddressAddressee',
      type: 'PlainText',
    }),
    Field({
      name: 'Billing Address Line 1',
      slug: 'billingAddressLine1',
      type: 'PlainText',
    }),
    Field({
      name: 'Billing Address Line 2',
      slug: 'billingAddressLine2',
      type: 'PlainText',
    }),
    Field({
      name: 'Billing Address City',
      slug: 'billingAddressCity',
      type: 'PlainText',
    }),
    Field({
      name: 'Billing Address State',
      slug: 'billingAddressState',
      type: 'PlainText',
    }),
    Field({
      name: 'Billing Address Postal Code',
      slug: 'billingAddressPostalCode',
      type: 'PlainText',
    }),
    Field({
      name: 'Billing Address Country',
      slug: 'billingAddressCountry',
      type: 'PlainText',
    }),
    Field({
      name: 'Shipping Address',
      slug: 'shippingAddress',
      type: 'PlainText',
    }),
    Field({
      name: 'User Items',
      slug: 'userItems',
      type: 'ItemRefSet',
      // @ts-expect-error - TS2322 - Type 'Record<{ collectionId: null; }> & Readonly<{ collectionId: null; }>' is not assignable to type 'Map<ValidationsKey, any> | undefined'.
      validations: ItemRefFieldValidation({
        // @ts-expect-error - TS2322 - Type 'string' is not assignable to type 'null | undefined'.
        collectionId: COMMERCE_ORDER_USER_ITEMS_COLLECTION_ID,
      }),
    }),
    Field({
      name: 'Shipping Methods',
      slug: 'availableShippingMethods',
      type: 'ItemRefSet',
      // @ts-expect-error - TS2322 - Type 'Record<{ collectionId: null; }> & Readonly<{ collectionId: null; }>' is not assignable to type 'Map<ValidationsKey, any> | undefined'.
      validations: ItemRefFieldValidation({
        // @ts-expect-error - TS2322 - Type 'string' is not assignable to type 'null | undefined'.
        collectionId: COMMERCE_ORDER_SHIPPING_METHODS_COLLECTION_ID,
      }),
    }),
    Field({
      name: 'Extra Items',
      slug: 'extraItems',
      type: 'ItemRefSet',
      // @ts-expect-error - TS2322 - Type 'Record<{ collectionId: null; }> & Readonly<{ collectionId: null; }>' is not assignable to type 'Map<ValidationsKey, any> | undefined'.
      validations: ItemRefFieldValidation({
        // @ts-expect-error - TS2322 - Type 'string' is not assignable to type 'null | undefined'.
        collectionId: COMMERCE_ORDER_EXTRA_ITEMS_COLLECTION_ID,
      }),
    }),
    Field({
      name: 'Shipping Method Name',
      slug: 'shippingMethodName',
      type: 'PlainText',
    }),
    Field({
      name: 'Shipping Method Description',
      slug: 'shippingMethodDescription',
      type: 'PlainText',
    }),
    Field({name: 'Count', slug: 'userItemsCount', type: 'Number'}),
    Field({name: 'Requires Shipping', slug: 'requiresShipping', type: 'Bool'}),
    Field({
      name: 'Additional TextInput',
      slug: 'additionalTextInput',
      type: 'PlainText',
    }),
    Field({
      name: 'Additional Textarea',
      slug: 'additionalTextArea',
      type: 'PlainText',
    }),
    Field({
      name: 'Additional Checkbox',
      slug: 'additionalCheckbox',
      type: 'Bool',
    }),
    Field({name: 'Comment', slug: 'comment', type: 'PlainText'}),
    Field({name: 'Has Downloads', slug: 'hasDownloads', type: 'Bool'})
  ),
  pluralName: 'Orders',
  singularName: 'Order',
});

export const COMMERCE_ORDER_CARD_FIELD_SLUGS = [
  'cardProvider',
  'cardLastFour',
  'cardExpiresMonth',
  'cardExpiresYear',
] as const;

export const COMMERCE_ORDER_COND_VIS_HIDDEN_FIELD_SLUGS = [
  'comment',
  'userItems',
  'extraItems',
  'shippingAddress',
  'availableShippingMethods',
] as const;

export const buildProductItemRefField = singleMemo((productsCollectionId) =>
  Field({
    name: 'Product',
    slug: 'product',
    type: 'ItemRef',
    // @ts-expect-error - TS2322 - Type 'Record<{ collectionId: null; }> & Readonly<{ collectionId: null; }>' is not assignable to type 'Map<ValidationsKey, any> | undefined'.
    validations: ItemRefFieldValidation({
      // @ts-expect-error - TS2322 - Type 'string' is not assignable to type 'null | undefined'.
      collectionId: productsCollectionId,
    }),
  })
) as (arg1: string) => FieldType;

export const buildSkuItemRefField = singleMemo((skuCollectionId) =>
  Field({
    name: 'SKU',
    slug: 'sku',
    type: 'ItemRef',
    // @ts-expect-error - TS2322 - Type 'Record<{ collectionId: null; }> & Readonly<{ collectionId: null; }>' is not assignable to type 'Map<ValidationsKey, any> | undefined'.
    validations: ItemRefFieldValidation({
      // @ts-expect-error - TS2322 - Type 'string' is not assignable to type 'null | undefined'.
      collectionId: skuCollectionId,
    }),
  })
) as (arg1: string) => FieldType;

// It is important that all the structures here are implemented with
// records as this collection is also used by DynBindingJsonUtils.
export const getCommerceOrderUserItemsCollection = (
  productsCollectionId: string,
  skuCollectionId: string
): CollectionRecordType =>
  CollectionRecord({
    _id: COMMERCE_ORDER_USER_ITEMS_COLLECTION_ID,
    slug: 'userItems',
    name: 'Products',
    fields: List.of(
      Field({name: 'Item subtotal', slug: 'rowTotal', type: 'CommercePrice'}),
      Field({name: 'Price', slug: 'price', type: 'CommercePrice'}),
      Field({
        name: 'Item quantity',
        slug: 'count',
        type: 'Number',
        // @ts-expect-error - TS2322 - Type 'Record<{ precision: null; format: null; }> & Readonly<{ precision: null; format: null; }>' is not assignable to type 'Map<ValidationsKey, any> | undefined'.
        validations: NumberFieldValidation({
          // @ts-expect-error - TS2322 - Type 'number' is not assignable to type 'null | undefined'.
          precision: 1,
          // @ts-expect-error - TS2322 - Type 'string' is not assignable to type 'null | undefined'.
          format: 'integer',
        }),
      }),
      Field({
        name: 'Subscription interval',
        slug: 'subscriptionInterval',
        type: 'Number',
        // @ts-expect-error - TS2322 - Type 'Record<{ precision: null; format: null; }> & Readonly<{ precision: null; format: null; }>' is not assignable to type 'Map<ValidationsKey, any> | undefined'.
        validations: NumberFieldValidation({
          // @ts-expect-error - TS2322 - Type 'number' is not assignable to type 'null | undefined'.
          precision: 1,
          // @ts-expect-error - TS2322 - Type 'string' is not assignable to type 'null | undefined'.
          format: 'integer',
        }),
      }),
      Field({
        name: 'Subscription frequency',
        slug: 'subscriptionFrequency',
        type: 'PlainText',
      }),
      Field({
        name: 'Trial period days',
        slug: 'subscriptionTrial',
        type: 'Number',
        // @ts-expect-error - TS2322 - Type 'Record<{ precision: null; format: null; }> & Readonly<{ precision: null; format: null; }>' is not assignable to type 'Map<ValidationsKey, any> | undefined'.
        validations: NumberFieldValidation({
          // @ts-expect-error - TS2322 - Type 'number' is not assignable to type 'null | undefined'.
          precision: 1,
          // @ts-expect-error - TS2322 - Type 'string' is not assignable to type 'null | undefined'.
          format: 'integer',
        }),
      }),
      buildProductItemRefField(productsCollectionId),
      buildSkuItemRefField(skuCollectionId)
    ),
    pluralName: 'Cart Items',
    singularName: 'Cart Item',
  });

export const COMMERCE_ORDER_SHIPPING_METHODS_COLLECTION = CollectionRecord({
  _id: COMMERCE_ORDER_SHIPPING_METHODS_COLLECTION_ID,
  slug: 'availableShippingMethods',
  name: 'Shipping Method',
  fields: List.of(
    Field({name: 'Name', slug: 'name', type: 'PlainText'}),
    Field({name: 'Description', slug: 'description', type: 'PlainText'}),
    Field({name: 'Price', slug: 'price', type: 'CommercePrice'})
  ),
  pluralName: 'Shipping Methods',
  singularName: 'Shipping Method',
});

export const COMMERCE_ORDER_EXTRA_ITEMS_COLLECTION = CollectionRecord({
  _id: COMMERCE_ORDER_EXTRA_ITEMS_COLLECTION_ID,
  slug: 'extraItems',
  name: 'Extra Items',
  fields: List.of(
    Field({name: 'Name', slug: 'name', type: 'PlainText'}),
    Field({name: 'Price', slug: 'price', type: 'CommercePrice'})
  ),
  pluralName: 'Extra Items',
  singularName: 'Extra Item',
});

export const COMMERCE_CART_COLLECTION = CollectionRecord({
  _id: COMMERCE_CART_COLLECTION_ID,
  slug: 'cart',
  name: 'Cart',
  fields: List.of(
    Field({
      name: 'Quantity',
      slug: 'userItemsCount',
      type: 'Number',
      // @ts-expect-error - TS2322 - Type 'Record<{ precision: null; format: null; }> & Readonly<{ precision: null; format: null; }>' is not assignable to type 'Map<ValidationsKey, any> | undefined'.
      validations: NumberFieldValidation({
        // @ts-expect-error - TS2322 - Type 'number' is not assignable to type 'null | undefined'.
        precision: 1,
        // @ts-expect-error - TS2322 - Type 'string' is not assignable to type 'null | undefined'.
        format: 'integer',
      }),
    }),
    Field({name: 'Subtotal', slug: 'subtotal', type: 'CommercePrice'})
  ),
  pluralName: 'Carts',
  singularName: 'Cart',
});

export const COMMERCE_PRODUCT_OPTIONS_COLLECTION = CollectionRecord({
  _id: COMMERCE_PRODUCT_OPTIONS_COLLECTION_ID,
  slug: 'productOptions',
  name: 'Product Options',
  fields: List.of(
    Field({
      name: 'Name',
      slug: 'name',
      type: 'PlainText',
    }),
    Field({
      name: 'Value',
      slug: 'value',
      type: 'PlainText',
    })
  ),
  pluralName: 'Product Options',
  singularName: 'Product Option',
});

export const COMMERCE_ADD_TO_CART_PRODUCT_OPTIONS_COLLECTION = CollectionRecord(
  {
    _id: COMMERCE_ADD_TO_CART_PRODUCT_OPTIONS_COLLECTION_ID,
    slug: 'atcProductOptions',
    name: 'Product Options',
    fields: List.of(
      Field({
        name: 'Name',
        slug: 'name',
        type: 'PlainText',
      })
    ),
    pluralName: 'Product Options',
    singularName: 'Product Option',
  }
);

export const COMMERCE_DOWNLOAD_FILES_COLLECTION = CollectionRecord({
  _id: COMMERCE_DOWNLOAD_FILES_COLLECTION_ID,
  slug: 'downloadFiles',
  name: 'Downloads',
  fields: List.of(
    Field({
      name: 'File name',
      slug: 'name',
      type: 'PlainText',
    }),
    Field({
      name: 'File URL',
      slug: 'url',
      type: 'Link',
    })
  ),
  pluralName: 'Downloads',
  singularName: 'Download',
});

export const COMMERCE_ORDER_COLLECTIONS_IDS = [
  COMMERCE_ORDER_ROOT_COLLECTION_ID,
  COMMERCE_ORDER_USER_ITEMS_COLLECTION_ID,
  COMMERCE_ORDER_SHIPPING_METHODS_COLLECTION_ID,
  COMMERCE_ORDER_EXTRA_ITEMS_COLLECTION_ID,
  COMMERCE_CART_COLLECTION_ID,
  COMMERCE_PRODUCT_OPTIONS_COLLECTION_ID,
  COMMERCE_DOWNLOAD_FILES_COLLECTION_ID,
  COMMERCE_ADD_TO_CART_PRODUCT_OPTIONS_COLLECTION_ID,
] as const;

export const getCommerceOrderCollections = singleMemo(
  (productsCollectionId: string) =>
    singleMemo((skuCollectionId: string): CollectionRecordType[] => [
      COMMERCE_ORDER_ROOT_COLLECTION,
      getCommerceOrderUserItemsCollection(
        productsCollectionId,
        skuCollectionId
      ),
      COMMERCE_ORDER_SHIPPING_METHODS_COLLECTION,
      COMMERCE_ORDER_EXTRA_ITEMS_COLLECTION,
      COMMERCE_CART_COLLECTION,
      COMMERCE_PRODUCT_OPTIONS_COLLECTION,
      COMMERCE_DOWNLOAD_FILES_COLLECTION,
      COMMERCE_ADD_TO_CART_PRODUCT_OPTIONS_COLLECTION,
    ]) as (arg1: string) => CollectionRecordType[]
);

export const getOrderItemsCollection = weakMemo(
  (
    collections: Map<string, CollectionRecordType>
  ): CollectionRecordType | null => {
    const productCollectionId = getProductCollectionId(collections);
    const skuCollectionId = getSkuCollectionId(collections);
    return typeof productCollectionId === 'string' &&
      typeof skuCollectionId === 'string'
      ? getCommerceOrderUserItemsCollection(
          productCollectionId,
          skuCollectionId
        )
      : null;
  }
);

export const getCartItemsSkuField = (
  collections: Map<string, CollectionRecordType>
): FieldType | null | undefined => {
  const orderItemsCollection = getOrderItemsCollection(collections);
  return orderItemsCollection
    ? orderItemsCollection.fields.find(
        (f) => f.get('slug') === COMMERCE_SKU_FIELD_SLUG
      )
    : null;
};

export const getCartItemsProductField = (
  collections: Map<string, CollectionRecordType>
): FieldType | null | undefined => {
  const orderItemsCollection = getOrderItemsCollection(collections);
  return orderItemsCollection
    ? orderItemsCollection.fields.find(
        (f) => f.get('slug') === COMMERCE_PRODUCT_FIELD_SLUG
      )
    : null;
};

export const getCartItemsSkuFieldBackend = (
  collections: Map<string, CollectionRecordType>
): FieldType | null | undefined => {
  const orderItemsCollection = getOrderItemsCollection(collections);
  return orderItemsCollection
    ? orderItemsCollection.fields.find(
        (f) => f.get('slug') === COMMERCE_SKU_FIELD_SLUG
      )
    : null;
};

export const getCartItemsProductFieldBackend = (
  collections: Map<string, CollectionRecordType>
): FieldType | null | undefined => {
  const orderItemsCollection = getOrderItemsCollection(collections);
  return orderItemsCollection
    ? orderItemsCollection.fields.find(
        (f) => f.get('slug') === COMMERCE_PRODUCT_FIELD_SLUG
      )
    : null;
};

export const withOrderItemsCollection = weakMemo(
  (collections: CollectionsType): CollectionsType => {
    const orderItemsCollection = getOrderItemsCollection(collections);
    return orderItemsCollection && orderItemsCollection._id
      ? collections.set(orderItemsCollection._id, orderItemsCollection)
      : collections;
  }
) as (arg1: CollectionsType) => CollectionsType;

export const isCommerceCollection = (
  collection: CollectionRecordType
): boolean => Boolean(collection) && collection.plugin === COMMERCE_PLUGIN_KEY;

export const isProductsCollection = (
  collection?: CollectionRecordType | null
): boolean =>
  collection != null &&
  isCommerceCollection(collection) &&
  collection.slug === COMMERCE_PRODUCT_COLLECTION_SLUG;

export const isSkusCollection = (
  collection?: CollectionRecordType | null
): boolean =>
  collection != null &&
  isCommerceCollection(collection) &&
  collection.slug === COMMERCE_SKU_COLLECTION_SLUG;

export const isCategoriesCollection = (
  collection?: CollectionRecordType | null
): boolean =>
  collection != null &&
  isCommerceCollection(collection) &&
  collection.slug === COMMERCE_CATEGORY_COLLECTION_SLUG;

export const isCommerceOrderCollection = (
  collection: CollectionRecordType
): boolean =>
  Boolean(collection) && collection._id === COMMERCE_ORDER_ROOT_COLLECTION_ID;

export const isCommerceCartCollection = (
  collection?: CollectionRecordType | null
): boolean =>
  Boolean(collection && collection._id === COMMERCE_CART_COLLECTION_ID);

export const isOrderItemsCollection = (
  collection: CollectionRecordType
): boolean =>
  Boolean(collection) &&
  collection._id === COMMERCE_ORDER_USER_ITEMS_COLLECTION_ID;

export const getProductCollectionId = (
  collections: CollectionsType
): string | null | undefined => {
  const productCollection = collections.find(isProductsCollection);
  return productCollection ? productCollection._id : null;
};

export const getSkuCollectionId = (
  collections: CollectionsType
): string | null | undefined => {
  const skuCollection = getSkuCollection(collections);
  return skuCollection ? skuCollection._id : null;
};

export const getProductCollection = (
  collections: CollectionsType
): CollectionRecordType | null | undefined =>
  collections.find(isProductsCollection);

export const getSkuCollection = (
  collections: CollectionsType
): CollectionRecordType | null | undefined =>
  collections.find(isSkusCollection);

export const getCategoriesCollection = (
  collections: CollectionsType
): CollectionRecordType | null | undefined =>
  collections.find(isCategoriesCollection);

export const getProductDefaultSkuField = (
  collections: CollectionsType
): FieldType | null | undefined => {
  const productCollection = getProductCollection(collections);
  return productCollection
    ? productCollection.fields.find((f) => f.get('slug') === DEFAULT_SKU_SLUG)
    : null;
};

export const isProductDefaultSkuField = (
  // @ts-expect-error - TS2749 - 'Field' refers to a value, but is being used as a type here. Did you mean 'typeof Field'?
  field: Field,
  collections: CollectionsType
): boolean => {
  return field === getProductDefaultSkuField(collections);
};

export const getProductNameField = (
  collections: CollectionsType
): FieldType | null | undefined => {
  const skuCollection = getSkuCollection(collections);
  return skuCollection
    ? skuCollection.fields.find((f) => f.get('slug') === 'name')
    : null;
};

export const getSkuImageField = (
  collections: CollectionsType
): FieldType | null | undefined => {
  const skuCollection = getSkuCollection(collections);
  return skuCollection
    ? skuCollection.fields.find((f) => f.get('slug') === 'main-image')
    : null;
};

export const getSkuPriceField = (
  collections: CollectionsType
): FieldType | null | undefined => {
  const skuCollection = getSkuCollection(collections);
  return skuCollection
    ? skuCollection.fields.find((f) => f.get('slug') === 'price')
    : null;
};

const getProductsBindingFieldSortValue = (
  fieldIdentifiers: Array<string>,
  fieldIdentifier: string
): number => {
  const slugIndex = fieldIdentifiers.indexOf(fieldIdentifier);
  return (slugIndex > -1 ? slugIndex : fieldIdentifiers.length) + 1;
};

export const getProductsBindingFieldSlugSortValue = (
  fieldIdentifier: string
): number => {
  const productSlugs = [
    ...getDefaultFieldSlugs(PRODUCTS),
    ...DEFAULT_DATETIME_FIELD_SLUGS,
  ];

  return getProductsBindingFieldSortValue(productSlugs, fieldIdentifier);
};

export const getProductsBindingFieldLabelSortValue = (
  fieldIdentifier: string
): number => {
  const productLabels = [
    'Type',
    'Name',
    'Slug',
    'Description',
    'Main Image',
    'Shippable',
    'Requires Shipping',
    'Price',
    'Compare-at price',
    'SKU',
    'Weight',
    'Width',
    'Length',
    'Height',
  ];

  return getProductsBindingFieldSortValue(productLabels, fieldIdentifier);
};

const orderItemFieldSlugsInOrder = [
  'name',
  'description',
  'price',
  'compare-at-price',
  'count',
  'rowTotal',
  'main-image',
  'shippable',
  'sku',
  'length',
  'width',
  'height',
  'weight',
  'created-on',
  'updated-on',
  'published-on',
];

const getOrderItemFieldsSortValue = (slug: string): number =>
  orderItemFieldSlugsInOrder.includes(slug)
    ? orderItemFieldSlugsInOrder.indexOf(slug)
    : orderItemFieldSlugsInOrder.length;

export const sortOrderItemFields = (pathLhs: any, pathRhs: any) => {
  return (
    // @ts-expect-error - TS2571 - Object is of type 'unknown'.
    getOrderItemFieldsSortValue(last(pathLhs).slug) -
    // @ts-expect-error - TS2571 - Object is of type 'unknown'.
    getOrderItemFieldsSortValue(last(pathRhs).slug)
  );
};

export const getAllFieldsForProducts = (
  collections: CollectionsType,
  collection?: CollectionRecordType
): Array<FieldType> => {
  const productCollection = collection
    ? collection
    : getProductCollection(collections);
  const productFields = productCollection
    ? productCollection.get('fields').toArray()
    : [];
  const skuCollection = getSkuCollection(collections);
  const skuFields = skuCollection ? skuCollection.get('fields').toArray() : [];

  // SKU fields contain fields that have the same slugs as Products,
  // we want to remove these duplicates
  const productFieldSlugsHash = productFields.reduce<Record<string, any>>(
    (hash, field) => {
      hash[field.get('slug')] = true;
      return hash;
    },
    {}
  );
  const dedupedSkuFields = skuFields.filter(
    (field) => !productFieldSlugsHash[field.get('slug')]
  );

  return [
    ...HARD_CODED_FIELDS_PER_COLLECTION_TYPE[PRODUCTS],
    ...productFields,
    ...dedupedSkuFields,
  ];
};

export const getAllDefaultFieldsForProducts = (
  collections: CollectionsType
): Array<FieldType> => {
  const allFields = getAllFieldsForProducts(collections);
  const defaultFieldSlugs = getDefaultFieldSlugs(PRODUCTS);

  return allFields.filter((field: FieldType) =>
    defaultFieldSlugs.includes(field.get('slug'))
  );
};

export const getAllVariantFieldsForProducts = (
  collections: CollectionsType
): Array<FieldType> => {
  const productFields = getAllFieldsForProducts(collections);
  const variantFields = DEFAULT_FIELD_SLUGS_FOR_PRODUCT_COLLECTION_VARIANTS.map(
    (slug) => productFields.find((field) => field.get('slug') === slug)
  ).filter(Boolean);

  return variantFields.map((field) => {
    // @ts-expect-error - TS18048 - 'field' is possibly 'undefined'.
    if (field.get('slug') === 'main-image') {
      // @ts-expect-error - TS18048 - 'field' is possibly 'undefined'.
      field = field.set('name', 'Variant image');
    }
    // @ts-expect-error - TS18048 - 'field' is possibly 'undefined'. | TS18048 - 'field' is possibly 'undefined'.
    return field.set('id', `variant-${field.get('id')}`);
  });
};

const getAllFieldsForCategories = (
  collections: CollectionsType,
  collection?: CollectionRecordType
): Array<FieldType> => {
  const categoryCollection = collection
    ? collection
    : getCategoriesCollection(collections);
  const categoryFields = categoryCollection
    ? categoryCollection.get('fields')
    : [];

  return [
    ...categoryFields,
    ...HARD_CODED_FIELDS_PER_COLLECTION_TYPE[CATEGORIES],
  ];
};

export const getAllDefaultFieldsForCategories = (
  collections: CollectionsType,
  collection?: CollectionRecordType
): Array<FieldType> => {
  const allFields = getAllFieldsForCategories(collections, collection);
  const defaultFieldSlugs = getDefaultFieldSlugs(CATEGORIES);

  return allFields.filter((field: FieldType) =>
    defaultFieldSlugs.includes(field.get('slug'))
  );
};

export const getSkuFieldBySlug = (
  collection: CollectionRecordType,
  collections: CollectionsType,
  fieldSlug: string
) => {
  if (isProductsCollection(collection)) {
    const skuCollection = getSkuCollection(collections);
    return skuCollection
      ? skuCollection.fields.find((f) => f.slug === fieldSlug) || null
      : null;
  }
  return null;
};

export const addInventoryFieldToSkuCollection = (
  collections: CollectionsType
) => {
  const skuCollection = getSkuCollection(collections);
  if (skuCollection) {
    if (
      skuCollection
        .get('fields')
        .some((f) => f.slug === 'ecSkuInventoryQuantity')
    ) {
      return collections;
    }
    return collections.updateIn(
      [skuCollection.get('_id'), 'fields'],
      (fields) =>
        fields.push(
          Field({
            name: 'Quantity',
            slug: 'ecSkuInventoryQuantity',
            type: 'Number',
            // @ts-expect-error - TS2322 - Type 'Record<{ precision: null; format: null; }> & Readonly<{ precision: null; format: null; }>' is not assignable to type 'Map<ValidationsKey, any> | undefined'.
            validations: NumberFieldValidation({
              // @ts-expect-error - TS2322 - Type 'number' is not assignable to type 'null | undefined'.
              precision: 1,
              // @ts-expect-error - TS2322 - Type 'string' is not assignable to type 'null | undefined'.
              format: 'integer',
            }),
          })
        )
    );
  }
  return collections;
};

export const setDefaultProductValues = <T extends Map<string, any>>(
  settings: Map<string, any>,
  item: T
): T => {
  // @ts-expect-error - TS2322 - Type 'Map<string, any>' is not assignable to type 'T'.
  return item
    .set('shippable', true)
    .set('price', null)
    .set(
      'tax-category',
      settings.get('defaultTaxCategory', DEFAULT_TAX_CATEGORY)
    )
    .set('ec-product-type', DEFAULT_PRODUCT_TYPE_ID);
};

export const getCategoriesProductsMap = (
  items: ItemsType,
  collections: CollectionsType
) => {
  const products = items
    .filter((item) => item.get('_cid') === getProductCollectionId(collections))
    .toJS();

  return reduce<any, {[categoryId: string]: string[]}>(
    products,
    (categoriesMap, productItem) => {
      const productItemCategories: string[] = productItem.category || [];
      if (!productItemCategories.length) {
        return categoriesMap;
      }

      productItemCategories.forEach((categoryId) => {
        categoriesMap[categoryId] = [
          ...(categoriesMap[categoryId] || []),
          productItem._id,
        ];
      });

      return categoriesMap;
    },
    {}
  );
};

export {
  didTheUserEnableCheckoutForThisSite,
  didTheUserSetUpCommerceForThisSite,
} from '@packages/systems/commerce/store';

export const commerceInlineFields = ['width', 'height', 'length'] as const;
export const commerceLargeFields = ['weight'] as const;

const ALL_COUNTRIES = commerceCountryList.map((country) => country.code);

/* keys are fields based on which value in item, the fields that are in arrays in values are shown or hidden */
export const dependentProductFields = {
  shippable: ['weight', 'width', 'height', 'length'],
} as const;

export const allSkuFields = List(ecommExtraFields.sku.map(reifyField));

type SettingConfigType = {
  slug: CommerceSettingsPanelSettingType;
  title: string;
  icon: Record<any, any>;
};

export const allSkuSlugs: string[] = allSkuFields
  .map((field: FieldType) => field.get('slug'))
  .toArray();

const excludeFromSkuSlugs = ['product', 'sku-values'];
export const fieldsFromSkuSlugs: Array<string> = allSkuSlugs.filter(
  (x) => x && !excludeFromSkuSlugs.includes(x)
);

export const getDimensionUnitForUnitType = (type: UnitSystemType) =>
  type === 'metric' ? 'cm' : 'in';
export const getWeightUnitForUnitType = (type: UnitSystemType) =>
  type === 'metric' ? 'g' : 'oz';

export const selectWidgetEmptyHolderText = {
  [COMMERCE_PRODUCT_COLLECTION_SLUG]: {
    header: 'No products in this collection',
    subheader: 'Products can be added from the search bar above',
  },
  [COMMERCE_CATEGORY_COLLECTION_SLUG]: {
    header: 'This product is not in a category yet',
    subheader: 'Add this product to a category from the search bar above',
  },
} as const;

const productDefaultFieldSlugs = ecommExtraFields.product.map(
  (field: {slug: FieldType['slug']}) => field.slug
);

const hardCodedProductFieldSlugs = HARD_CODED_FIELDS_PER_COLLECTION_TYPE[
  COLLECTION_TYPES.PRODUCTS
].map((field: FieldType) => field.slug);

export const ecommerceReservedSlugs = [
  ...productDefaultFieldSlugs,
  ...hardCodedProductFieldSlugs,
  ...allSkuSlugs,
  // UI field on product form is called `Required shipping` (with slug of `shippable`)
  // conditional visibility list is sorted (see `sortByProductSection`) by the slug
  // and the slug is based off the name of the field, so we need to reserve this slug
  'requires-shipping',
] as const;

export const categoryDefaultFieldSlugs: Array<FieldType['slug']> =
  ecommExtraFields.category.map(
    (field: {slug: FieldType['slug']}) => field.slug
  );

export const allProductDefaultFieldSlugs = [
  ...productDefaultFieldSlugs,
  ...fieldsFromSkuSlugs,
] as const;

export const getDefaultCommerceFieldsSlugs = (
  collection: CollectionRecordType
) => {
  if (isCategoriesCollection(collection)) {
    return categoryDefaultFieldSlugs;
  }

  if (isProductsCollection(collection)) {
    return allProductDefaultFieldSlugs;
  }

  return emptyArray;
};

export const isVisibleCommerceCollection = (collection: CollectionRecordType) =>
  isCommerceCollection(collection) &&
  (isCategoriesCollection(collection) || isProductsCollection(collection));

export const isNotCommerceCollection = negate(isCommerceCollection);

export const excludeCommerceCollections = <
  T extends CollectionsType | List<CollectionRecordType>,
>(
  collections: T
  // @ts-expect-error - TS2322 - Type 'Iterable<string, CollectionRecordType> | Iterable<number, CollectionRecordType>' is not assignable to type 'T'. | TS2345 - Argument of type '(collection: CollectionRecordType) => boolean' is not assignable to parameter of type '((value?: CollectionRecordType | undefined, key?: string | undefined, iter?: Iterable<string, CollectionRecordType> | undefined) => boolean) & ((value?: CollectionRecordType | undefined, key?: number | undefined, iter?: Iterable<...> | undefined) => boolean)'.
): T => collections.filter(isNotCommerceCollection);

export const onlyCommerceCollections = (
  collections: CollectionsType
): CollectionsType => collections.filter(isCommerceCollection).toMap();

export const onlyVisibleCommerceCollections = (
  collections: CollectionsType
): CollectionsType => collections.filter(isVisibleCommerceCollection);

export const addCategoryToProduct = (
  product: ItemRecordType,
  categoryId: string
): ItemRecordType =>
  product.set(
    'category',
    (product.get('category') || List([])).push(categoryId)
  );
export const removeCategoryFromProduct = (
  product: ItemRecordType,
  categoryId: string
) =>
  product.set(
    'category',
    // @ts-expect-error - TS7006 - Parameter 'id' implicitly has an 'any' type.
    product.get('category').filter((id) => id !== categoryId)
  );
export const toggleCategoryInProduct = (
  product: ItemRecordType,
  categoryId: string
): ItemRecordType =>
  product.get('category') && product.get('category').includes(categoryId)
    ? removeCategoryFromProduct(product, categoryId)
    : addCategoryToProduct(product, categoryId);

export const getSkuItem = (
  item: Map<string, any>,
  items: ItemRecordType[]
): Map<string, any> =>
  (
    items.find(
      (skuItem) => skuItem.get('_id') === item.get(DEFAULT_SKU_SLUG)
    ) || Map()
  ).filter((value, key) => fieldsFromSkuSlugs.includes(key));

export const getProductImageUrl = ({
  productItem,
  items,
  getSmallestImage,
}: {
  productItem: Map<string, string>;
  items: ItemsType;
  getSmallestImage: boolean;
}) => {
  const variants = items.getIn(
    [
      String(productItem.get(DEFAULT_SKU_SLUG)),
      'main-image',
      'file',
      'variants',
    ],
    null
  ) as List<RecordOf<Variant>> | Variant[] | null;

  const thumbUrl = items.getIn(
    [
      String(productItem.get(DEFAULT_SKU_SLUG)),
      'main-image',
      'file',
      'thumbUrl',
    ],
    null
  );

  const url = productItem.get(DEFAULT_SKU_SLUG)
    ? items.getIn(
        // String is used here only to remove flow error as it is currently not validating that value is a string :( https://github.com/facebook/flow/issues/2751
        [String(productItem.get(DEFAULT_SKU_SLUG)), 'main-image', 'url'],
        null
      )
    : null;

  if (getSmallestImage && thumbUrl) {
    // first we'll try to grab a thumbUrl from the file
    return thumbUrl;
  } else if (getSmallestImage && Array.isArray(variants) && variants.length) {
    // next, for newly uploaded images prior to saving an item, the variants come in as an array
    const cdnUrl = s3UrlToCdn(variants[0]?.s3Url || '');
    if (cdnUrl.length) {
      return cdnUrl;
    }
  } else if (getSmallestImage && List.isList(variants)) {
    // finally, if no thumbUrl and it's not a new image, we'll grab the smallest variant
    const firstVariant = variants.first();
    const cdnUrl = firstVariant
      ? s3UrlToCdn(firstVariant.get('s3Url') ?? '')
      : url;
    if (cdnUrl?.length) {
      return cdnUrl;
    }
  }

  return url;
};

export const addSkuFieldsToProduct = (
  productsCollection: CollectionRecordType,
  collections: CollectionsType
): CollectionRecordType => {
  const skuCollection = collections.find(isSkusCollection);
  return productsCollection.update('fields', (fields) =>
    fields.concat<FieldType>(
      skuCollection
        .get('fields')
        .filter((field) => fieldsFromSkuSlugs.includes(field.get('slug')))
    )
  );
};

export const removeSkuFieldsFromProduct = (
  productsCollection: CollectionRecordType
): CollectionRecordType =>
  productsCollection.update('fields', (fields) =>
    fields.filter((field) => !fieldsFromSkuSlugs.includes(field.get('slug')))
  );

export const clearCurrentCollection = (collection: CollectionRecordType) =>
  isProductsCollection(collection)
    ? removeSkuFieldsFromProduct(collection)
    : collection;

export const divideSkuAndProductItem = (
  item: Map<string, any>
): {
  productItem: Map<string, any>;
  skuItem: Map<string, any>;
} => {
  const dividedItems = item.groupBy((value, key) =>
    fieldsFromSkuSlugs.includes(key)
  );
  const productItem = dividedItems.get(false) || Map();
  const skuItem = dividedItems.get(true) || Map();

  // @ts-expect-error - TS2322 - Type 'Iterable<string, any>' is not assignable to type 'Map<string, any>'. | TS2322 - Type 'Iterable<string, any>' is not assignable to type 'Map<string, any>'.
  return {productItem, skuItem};
};

export const discardVariantFields = <T>(item: Map<string, T>): Map<string, T> =>
  item.filter((value, key) => !fieldsFromSkuSlugs.includes(key));

/*</> fix VSCode syntax highlighting that breaks from above func */

const _setIdToNullIfMint = (value: Map<string, any>): Map<string, any> =>
  isMintId(value.get('id')) ? value.set('id', null) : value;

// When passing to the backend, ids need to be explicitly `null` so that
// the backend generates id's for them. This function replaces temporary mint ids
// with null in preparation for sending new options (sku-properties) to
// the backend.
export const removeSkuPropertyMintIds = (
  optionSets: List<Map<string, any>> | undefined | null
) =>
  optionSets?.map((optionSet) =>
    _setIdToNullIfMint(
      optionSet.set(
        'enum',
        (optionSet.get('enum') || List()).map(_setIdToNullIfMint)
      )
    )
  ) || [];

export const fieldUnitGetters = {
  width: getDimensionUnitForUnitType,
  height: getDimensionUnitForUnitType,
  length: getDimensionUnitForUnitType,
  weight: getWeightUnitForUnitType,
} as const;

export const getSectionTitle = (
  settingsConfig: Array<SettingConfigType>,
  slug: CommerceSettingsPanelSettingType
) => {
  const selectedSetting = settingsConfig.find(
    (setting) => setting.slug === slug
  );
  return selectedSetting ? selectedSetting.title : '';
};

export const getRestOfTheWorldCountries = (
  shippingZones: List<ValidShippingZoneType>
): List<string> => {
  const countriesHandledByOtherZones = shippingZones
    .filter((_zone) => !_zone.get('isRestOfTheWorld'))
    .map((_zone) => _zone.get('countries') || List([]))
    .flatten()
    .toSet();

  return List(ALL_COUNTRIES).filter(
    (country) => !countriesHandledByOtherZones.has(country)
  );
};

export const getUpdatedShippingZones = (
  currentShippingZone: ValidShippingZoneType,
  shippingZones: List<ValidShippingZoneType>
) => {
  // remove fields from currentShippingZone that are not needed to be saved
  const currentShippingZoneData = pick(
    ['name', 'countries', 'methods', 'isRestOfTheWorld'],
    currentShippingZone
  );

  const currentShippingZoneIndex = currentShippingZone.get('_id')
    ? shippingZones.findIndex(
        (item) => item.get('_id') === currentShippingZone.get('_id')
      )
    : null;

  // if currentShippingZoneIndex is a number, then we are editing the existing shipping zone
  if (typeof currentShippingZoneIndex === 'number') {
    return shippingZones.update(currentShippingZoneIndex, (zone) =>
      currentShippingZoneData.set('_id', zone.get('_id'))
    );
    // if currentShippingZoneIndex is not a number, then we are creating new shipping zone
  } else {
    return shippingZones.push(currentShippingZoneData);
  }
};

export const hasCurrentShippingZoneChanged = (
  currentShippingZone: ValidShippingZoneType | null,
  shippingZones: List<ValidShippingZoneType>
) => {
  if (
    currentShippingZone === null ||
    currentShippingZone.size === 0 ||
    (currentShippingZone.size === 1 && currentShippingZone.get('name') === '')
  ) {
    return false;
  }

  if (!currentShippingZone.get('_id')) {
    return true;
  }

  const savedCurrentZone = shippingZones.find(
    (item) =>
      currentShippingZone && item.get('_id') === currentShippingZone.get('_id')
  );

  return !savedCurrentZone.equals(currentShippingZone);
};

export const getStripeName = (commerceState: CommerceStateShape) => {
  const displayName = commerceState.status.getIn([
    'commerceAuth',
    'displayName',
  ]);
  return displayName ? `(${displayName})` : '';
};

export const isLastRangeSet = (shippingMethod: ShippingMethodType) => {
  const rules = shippingMethod.get('rules');
  if (rules && typeof rules !== 'string') {
    const lastRule = rules.last();
    const isValueSet = (value: any) => value || value === 0;

    return (
      lastRule &&
      isValueSet(lastRule.getIn(['minValue', 'value'])) &&
      isValueSet(lastRule.getIn(['amount', 'value']))
    );
  }
  return false;
};

export const getGettingStartedTodosDetails = (
  gettingStartedStatusState: GettingStartedStatusStateShape
) => {
  return (
    gettingStartedStatusState
      // @ts-expect-error - TS2339 - Property 'toMap' does not exist on type 'GettingStartedStatusStateShape'.
      .toMap()
      // @ts-expect-error - TS7006 - Parameter 'status' implicitly has an 'any' type. | TS7006 - Parameter 'val' implicitly has an 'any' type. | TS7006 - Parameter 'key' implicitly has an 'any' type.
      .reduce<Record<string, any>>((status, val, key) => {
        if (Object.values(GETTING_STARTED_TO_DO_STATUSES).includes(key)) {
          status[key] = val;
        }
        return status;
      }, {})
  );
};

export const getGettingStartedTodosCompleted = (
  gettingStartedStatusState: GettingStartedStatusStateShape
) => {
  // @ts-expect-error - TS2339 - Property 'toMap' does not exist on type 'GettingStartedStatusStateShape'. | TS7006 - Parameter 'total' implicitly has an 'any' type. | TS7006 - Parameter 'val' implicitly has an 'any' type. | TS7006 - Parameter 'key' implicitly has an 'any' type.
  return gettingStartedStatusState.toMap().reduce((total, val, key) => {
    if (Object.values(GETTING_STARTED_TO_DO_STATUSES).includes(key)) {
      return val ? total + 1 : total;
    } else {
      return total;
    }
  }, 0);
};

export const getGettingStartedTodosRemaining = (
  commerceState: CommerceStateShape
): number => {
  const checkoutEnabled = commerceState.settings.checkoutEnabled;
  const gettingStartedStatus = commerceState.gettingStartedStatus;
  const checkoutEnabledRemaining = checkoutEnabled ? 1 : 0;

  const commerceGettingStartedTodosRemaining =
    Object.keys(GETTING_STARTED_TO_DO_STATUSES).length -
    getGettingStartedTodosCompleted(gettingStartedStatus) -
    checkoutEnabledRemaining;

  return commerceGettingStartedTodosRemaining;
};

export const countVariantsRemovedByDeletingAnOptionSet = ({
  optionSets,
  optionSetId,
}: {
  optionSets: List<Map<string, any>>;
  optionSetId: string;
}) => {
  // We want to count:
  //   Let N = total number of variants
  //   Let M = total number of variants without option set with id `optionSetId`
  //   So the return value should be N - M
  //
  // Let c_x = count of options for option set at 1-based index x
  // Let n = number of option sets
  // then N = c_1 * c_2 * ... *  c_n
  //
  // let j = index of option set with id `optionSetId`
  // then M = c_1 * c_2 * ... * c_(j - 1) * c_(j + 1) * ... * c_n
  // (compared to N, c_j is omitted)
  //
  // we can factor out the expression `N - M` with their common factor
  // (i.e. a * b * c - a * b = a * b * (c - 1))
  //
  // N - M = (c_1 * c_2 * ... * c_(j - 1) * c_(j + 1) * ... * c_n) * (c_j - 1)
  // = M * (c_j - 1)
  const countVariantsWithoutOptionSet = _productOfOptionCounts(
    optionSets.filter((_optionSet) => {
      const isOptionSetToBeOmitted = _optionSet.get('id') === optionSetId;
      return !isOptionSetToBeOmitted;
    })
  );
  const optionSet = optionSets.find(
    (_optionSet) => _optionSet.get('id') === optionSetId
  );

  // This shouldn't really happen, but this satisfies Flow.
  if (!optionSet) {
    // Please see https://github.com/webflow/webflow/pull/39900
    // eslint-disable-next-line webflow/better-bug-reporting
    reportBug('OptionSetNotFound', 'optionSet unexpectedly not found');
    // Return something as close as possible to the real value
    return countVariantsWithoutOptionSet;
  }

  // For the purpose of counting variants here, 0 options or 1 option is the
  // same (in both cases, there is no choice to be made). Normalizing to 1
  // allows the calculation to work correctly.
  const optionCount = Math.max(1, (optionSet.get('enum') || List()).size);

  return countVariantsWithoutOptionSet * (optionCount - 1);
};

export const countVariantsRemovedByDeletingAnOption = ({
  optionSets,
  optionSetId,
}: {
  optionSets: List<Map<string, any>>;
  optionSetId: string;
}): number => {
  // We want to count:
  //   Let N = total number of variants
  //   Let P = total number of variants with one fewer option in option set with id `optionSetId`
  //   So the return value should be N - P
  //
  // Let c_x = count of options for option set at 1-based index x
  // Let n = number of option sets
  // then N = c_1 * c_2 * ... *  c_n
  //
  // let j = the index of option set with id `optionSetId`
  // then P = c_1 * c_2 * ... * c_(j - 1) * (c_j - 1) * c_(j + 1) * ... * c_n
  // (compared to N, c_j is replaced with c_j - 1)
  //
  // we can factor out the expression `N - P` with their common factor
  // (i.e. a * b * c - a * b * (c - 1)
  //    = a * b * (c - (c - 1))
  //    = a * b * (c - c + 1)
  //    = a * b
  //
  // N - P = (c_1 * c_2 * ... * c_(j - 1) * c_(j + 1) * ... * c_n) * (c_j - (c_j - 1))
  // = (c_1 * c_2 * ... * c_(j - 1) * c_(j + 1) * ... * c_n)
  // = in other words, the arithmetic product of all option counts except for option set
  //   with id `optionSetId`
  return _productOfOptionCounts(
    optionSets.filter((_optionSet) => {
      const isOptionSetToBeOmitted = _optionSet.get('id') === optionSetId;
      return !isOptionSetToBeOmitted;
    })
  );
};

// We want to count:
//   Let N = total number of variants
//   Let P = total number of variants with one more option in option set with id `optionSetId`
//   So the return value should be P - N
//
// Let c_x = count of options for option set at 1-based index x
// Let n = number of option sets
// then N = c_1 * c_2 * ... *  c_n
//
// let j = the index of option set with id `optionSetId`
// then P = c_1 * c_2 * ... * c_(j - 1) * (c_j + 1) * c_(j + 1) * ... * c_n
// (compared to N, c_j is replaced with c_j + 1)
//
// we can factor out the expression `P - N` with their common factor
// (i.e. a * b * (c + 1) - a * b * c
//    = a * b * ((c + 1) - c)
//    = a * b * 1
//    = a * b
//
// P - N = (c_1 * c_2 * ... * c_(j - 1) * c_(j + 1) * ... * c_n) * ((c_j + 1) - c_j)
// = (c_1 * c_2 * ... * c_(j - 1) * c_(j + 1) * ... * c_n)
// = in other words, the arithmetic product of all option counts except for option set
//   with id `optionSetId`. And that happens to be the same calculation as
//   `countVariantsRemovedByDeletingAnOption`.
export const countVariantsCreatedByAddingAnOption =
  countVariantsRemovedByDeletingAnOption;

const _productOfOptionCounts = (optionSets: List<Map<string, any>>): number =>
  optionSets
    .map(
      // Converts 0 enum size to 1. An option set may temporarily have 0 options
      // on the frontend, which means that they're not entered by the user yet.
      // In this case, we can replace that 0 with a 1, so that we get an accurate number
      // for the product.
      (optionSet) =>
        Math.max(1, (optionSet.get('enum') || List()).size) /* c_x */
    )
    .reduce((a, b) => a * b, 1);

export const countVariants = _productOfOptionCounts;

// This delimiter must not clash with mongo object id's
// formatted as strings or our mint ids. The former is
// a hexstring and the latter is a combination of ASCII
// letters, digits and `-`.
const _OPTION_COMBINATION_HASH_DELIMITER = '%';

// This is a hash function to produce a hash value for a given option combination.
// This allows us to keep a hash map where keys are option combinations with the
// property of not having duplicate option combinations.
//
// Example:
//
// ```js
// > getOptionCombinationHash({color: 'red', size: 'medium'})
// 'color%red%size%medium'
// ```
// (the keys and values are not really plain words like that; they are IDs, but the
// same logic applies)
//
// The hash is just a concatenation of K0 + D + V0 + D + K1 + D + V1 ... Kn + D + Vn
// Where D is a delimiter character, Ki is the option set id (a key in OptionCombination)
// at index `i` when ordered lexicographically, and Vi is the associated option id (value in
// optionCombination).
export const getOptionCombinationHash = (
  optionCombination: OptionCombination
): OptionCombinationHash => {
  const keys = Object.keys(optionCombination);
  keys.sort();
  return keys
    .reduce<Array<any>>((parts, key, idx) => {
      if (key.includes(_OPTION_COMBINATION_HASH_DELIMITER)) {
        throw new Error(
          `Invalid option set id ${key}: contains hash delimiter`
        );
      }
      parts.push(key);
      parts.push(_OPTION_COMBINATION_HASH_DELIMITER);
      const optionId = optionCombination[key];

      // @ts-expect-error - TS18048 - 'optionId' is possibly 'undefined'.
      if (optionId.includes(_OPTION_COMBINATION_HASH_DELIMITER)) {
        throw new Error(
          `Invalid option id ${optionId}: contains hash delimiter`
        );
      }
      parts.push(optionCombination[key]);
      const isLast = idx === keys.length - 1;
      if (!isLast) {
        parts.push(_OPTION_COMBINATION_HASH_DELIMITER);
      }
      return parts;
    }, [])
    .join('');
};

// Scenario:
// A person has created a Shirt product with option set "Size (Small, Medium, Large)".
// That product and its variants is already being sold. Later, they want to add a sleeve
// type as an option set "Sleeve type (Long, Short)". The existing shirts had long sleeves.
//
// Before adding sleeve type, the generated variants are just Small, Medium, and Large.
// When we add a sleeve type, for each existing variant, there will be a Long and Short sleeve
// version. The existing variants can (and should) be reused, because they actually had long sleeves,
// and we should only generate new variants for the short sleeves. This will allow a Small Shirt
// that is in the shopping cart of a customer correctly stay there and be updated with the long sleeve,
// the default option, instead of that item disappearing from their shopping cart.
//
// This function thus assigns the default option that existing variants have to their 'sku-values'
// property.
export const assignDefaultOptionToExistingVariants = (
  variantsByOptionCombinationHash: VariantsByOptionCombinationHash,
  optionSetId: string,
  optionId: string
): VariantsByOptionCombinationHash => {
  // @ts-expect-error - TS2740 - Type 'Keyed<string, Variant>' is missing the following properties from type 'Map<string, Variant>': set, delete, remove, clear, and 14 more.
  return variantsByOptionCombinationHash.mapEntries(
    // @ts-expect-error - TS2345 - Argument of type '([_optionCombinationHash, variant]: [any, any]) => any[]' is not assignable to parameter of type '(entry?: any[] | undefined, index?: number | undefined, iter?: Keyed<string, Variant> | undefined) => any[]'.
    ([_optionCombinationHash, variant]: [any, any]) => {
      const newOptionCombination = {
        ...variant['sku-values'],
        [optionSetId]: optionId,
      } as const;
      const newVariant = {
        ...variant,
        'sku-values': newOptionCombination,
      } as const;
      return [getOptionCombinationHash(newOptionCombination), newVariant];
    }
  );
};

// Scenario:
// The user has created a Shirt product with option sets "Size (Small, Medium, Large)" and
// "Sleeve type (Long, Short)". They want to remove the Sleeve type option set, because they've
// stopped producing short sleeves. The remaining products will have long sleeves.
//
// After removing the Sleeve type option, only three variants will remain: Small, Medium, and Large.
// Half of the existing variants, those with the "Sleeve type: Long" option, can (and should) be
// reused. That way, if a customer had a small shirt with long sleeves in their shopping cart, it won't
// disappear from there, but will be displayed as just a small shirt.
//
// This function assumes that the "Sleeve type: Short" option has already been removed, and the remaining
// option set is effectively "Sleeve type (Long)". When we're removing the option set at this point, we just
// need to remove the `Long` option, the default option, from the 'sku-values' property of each of the
// existing variants. This function does just that.
export const removeDefaultOptionFromExistingVariants = (
  variantsByOptionCombinationHash: VariantsByOptionCombinationHash,
  optionSetId: string
): VariantsByOptionCombinationHash => {
  // @ts-expect-error - TS2322 - Type 'Keyed<string, Variant>' is not assignable to type 'VariantsByOptionCombinationHash'.
  return variantsByOptionCombinationHash.mapEntries(
    // @ts-expect-error - TS2345 - Argument of type '([_optionCombinationHash, variant]: [any, any]) => any[]' is not assignable to parameter of type '(entry?: any[] | undefined, index?: number | undefined, iter?: Keyed<string, Variant> | undefined) => any[]'.
    ([_optionCombinationHash, variant]: [any, any]) => {
      const newOptionCombination = {...variant['sku-values']} as const;
      // eslint-disable-next-line  webflow/no-delete
      delete newOptionCombination[optionSetId];
      const newVariant = {
        ...variant,
        'sku-values': newOptionCombination,
      } as const;
      return [getOptionCombinationHash(newOptionCombination), newVariant];
    }
  );
};

// This function should only be called when:
// 1. The option set has already been created.
// 2. The option set has at least one existing option.
export const variantsFromNewOption = (
  variantsByOptionCombinationHash: VariantsByOptionCombinationHash,
  optionSetId: string,
  optionId: string,
  mintIds: Array<string>
): VariantsByOptionCombinationHash => {
  let mintIdsUsed = 0;
  const mutableVariantsByOptionCombinationHash =
    variantsByOptionCombinationHash.asMutable();
  for (const variant of variantsByOptionCombinationHash.values()) {
    const newOptionCombination = {
      ...variant['sku-values'],
      [optionSetId]: optionId,
    } as const;
    const hash = getOptionCombinationHash(newOptionCombination);

    // E.g. if the current option set is "Size (Small, Medium)"
    // and we're adding "Large", Then both the existing variants
    // for Small and Medium will generate an option combination
    // of {Size: Large}
    if (!mutableVariantsByOptionCombinationHash.has(hash)) {
      const mintId = mintIds[mintIdsUsed];
      mintIdsUsed += 1;
      const newVariant = {
        ...variant,
        inventory: {
          inventoryType: variant.inventory
            ? variant.inventory.inventoryType
            : INFINITE_INVENTORY,
          quantity: 0,
        },
        _id: mintId,
        'sku-values': newOptionCombination,
      } as Variant;
      mutableVariantsByOptionCombinationHash.set(hash, newVariant);
    }
  }
  return mutableVariantsByOptionCombinationHash.asImmutable();
};

// This function should only be called when the option set that the option to remove
// belongs to will have one or more remaining options remaining after the removal.
// This is because removing the last option (or the default option)
// must follow a different logic.
export const variantsFromRemovedOption = (
  variantsByOptionCombinationHash: VariantsByOptionCombinationHash,
  optionSetId: string,
  optionId: string
): VariantsByOptionCombinationHash => {
  return variantsByOptionCombinationHash.filter(
    (variant) => variant['sku-values'][optionSetId] !== optionId
  );
};

export const generateVariantsByOptionCombinationHash = (
  variants: Array<Variant>
): VariantsByOptionCombinationHash => {
  return Map(
    variants.map((variant) => [
      getOptionCombinationHash(variant['sku-values']),
      variant,
    ])
  );
};

export function optionCombinationsFromOptionSets(
  optionSets: List<Map<string, any>>
): Array<OptionCombination> {
  return baseOptionCombinationsFromOptionSets(optionSets.toJS());
}

export const getOptionCombinationNames = (
  variantSkuValues: OptionCombination,
  optionSets: List<Map<string, any>>
): List<string> => {
  // An option set may have no options when a user is in the process of adding
  // an option set.
  const nonEmptyOptionSets = optionSets.filter(
    (optionSet) => optionSet.get('enum', List()).size > 0
  );
  const deterministicOrderOptionSets = nonEmptyOptionSets.sortBy((optionSet) =>
    optionSet.get('id')
  );

  return deterministicOrderOptionSets.map((optionSet) => {
    const optionSetName = optionSet.get('name', '');
    const optionId = variantSkuValues[optionSet.get('id', '')];
    const option = optionSet
      .get('enum', List())
      // @ts-expect-error - TS7006 - Parameter 'option_' implicitly has an 'any' type.
      .find((option_) => option_.get('id') === optionId);

    if (!option) {
      // prevents crash (issue #30611) if variants are corrupted (addressed in PR #34610)
      return `${optionSetName}: Undefined`;
    }

    return `${optionSetName}: ${option.get('name')}`;
  });
};

export const getVariantName = (
  variantSkuValues: OptionCombination,
  product: Map<string, any>
): string => {
  return baseGetVariantName(variantSkuValues, product.toJS());
};

// This can't be derived from the variant name because slugs must
// be unique, but names don't have to be. E.g. if a user creates
// two products with the name "Dishwasher", they will have slugs
// "dishwasher" and "dishwasher-2". This `-2` uniqueness suffix
// can only be found in the slugs, hence we can't just slugify the
// product name + option names to get a slug that's unique for
// the whole variants collection across products (a current limitation).
export const getVariantSlug = (
  variantSkuValues: OptionCombination,
  product: Map<string, any>
): string => {
  return baseGetVariantSlug(variantSkuValues, product.toJS());
};

// This function is used in the variants persistence flow. We have a factory function here,
// instead of a function that takes the old option sets, persisted option sets and a variant,
// so that we can use the same memo-map of (UnsavedId -> SavedId) for all variants instead of
// looking them manually for each variant, of which there can be many.
export const makeOptionCombinationUpdater = ({
  unsavedOptionSets,
  savedOptionSets,
}: {
  unsavedOptionSets: SkuPropertiesRecordType | undefined | null;
  savedOptionSets: SkuPropertiesRecordType;
}) => {
  const savedIdsByMintId =
    unsavedOptionSets?.reduce<Record<string, any>>(
      (acc, unsavedOptionSet, idx) => {
        const id = unsavedOptionSet.get('id', '');
        const savedOptionSet = savedOptionSets.get(idx);
        if (!savedOptionSet) {
          throw new Error('Unexpected');
        }
        if (isMintId(id)) {
          acc[id] = savedOptionSet.get('id');
        }

        unsavedOptionSet.get('enum', List()).forEach((option, optionIdx) => {
          const optionId = option.get('id');
          if (isMintId(optionId)) {
            const savedOptions = savedOptionSet.get('enum') || List();
            const savedOption = savedOptions.get(optionIdx);
            if (!savedOption) {
              throw new Error('Unexpected');
            }
            acc[optionId] = savedOption.get('id');
          }
        });
        return acc;
      },
      {}
    ) || {};
  return function updateOptionCombination(
    optionCombination: OptionCombination
  ): OptionCombination {
    return Object.keys(optionCombination).reduce<Record<string, any>>(
      (acc, key) => {
        let newKey = key;
        let newValue = optionCombination[key];
        if (isMintId(key)) {
          newKey = savedIdsByMintId[key];
          if (!newKey) {
            throw new Error('Unexpected');
          }
        }
        if (isMintId(newValue)) {
          newValue = newValue ? savedIdsByMintId[newValue] : null;
          if (!newValue) {
            throw new Error('unexpected');
          }
        }
        acc[newKey] = newValue;
        return acc;
      },
      {}
    );
  };
};

export const getVariantsToDelete = ({
  persistedVariants,
  newVariants,
}: {
  persistedVariants: Array<Variant>;
  newVariants: Array<Variant>;
}): Array<Variant> => {
  const stillExistingIds = newVariants.map((variant) => variant._id);
  return persistedVariants.filter(
    (variant) => !stillExistingIds.includes(variant._id)
  );
};

// In our logic, an undefined inventory is the same as an infinite inventory.
export const normalizeInventory = (inventory?: Inventory | null): Inventory => {
  if (!inventory) {
    return INFINITE_INVENTORY;
  }
  if (inventory.inventoryType === INVENTORY_TYPE_INFINITE) {
    if (inventory.quantity === 0) {
      // Everything's normalized.
      return inventory;
    }
    return {
      inventoryType: INVENTORY_TYPE_INFINITE,
      quantity: 0,
    };
  }
  return inventory;
};

// The data that the inventory update endpoints accepts
type InventoryUpdateSpec =
  | Inventory
  | {
      inventoryType: typeof INVENTORY_TYPE_FINITE;
      updateQuantity: number;
    };

// Returns null if no update is needed, otherwise the argument data to the inventory update endpoint.
// Handles inventory updates with deltas: if a shop owner enters a product page with 10 items of inventory,
// and sets it to 15 items, then when saving we tell the backend to increment the inventory by 5 items
// instead of setting it to 15. This correctly handles the edge case if the inventory was decreased after entering
// the product page, but before submitting the update.
export const determineInventoryUpdate = (
  oldInventory: Inventory,
  newInventory: Inventory
): null | InventoryUpdateSpec => {
  if (
    oldInventory.inventoryType === INVENTORY_TYPE_FINITE &&
    newInventory.inventoryType === INVENTORY_TYPE_FINITE
  ) {
    // The most common update - finite -> finite
    const delta = newInventory.quantity - oldInventory.quantity;
    if (delta === 0) {
      // no update necessary
      return null;
    }
    return {
      inventoryType: INVENTORY_TYPE_FINITE,
      updateQuantity: delta,
    };
  } else if (
    oldInventory.inventoryType === INVENTORY_TYPE_INFINITE &&
    newInventory.inventoryType === INVENTORY_TYPE_INFINITE
  ) {
    // infinite -> infinite, No need to update.
    return null;
  } else if (oldInventory.inventoryType === INVENTORY_TYPE_INFINITE) {
    // infinite -> finite
    return newInventory;
  } else if (oldInventory.inventoryType === INVENTORY_TYPE_FINITE) {
    // finite -> infinite
    return INFINITE_INVENTORY;
  } else {
    throw new Error('Unhandled case');
  }
};

export const findRelatedSkuItems = (
  product: Map<string, any>,
  collectionState: CollectionStateType,
  locale?: Locale
): List<Map<string, any>> => {
  const {collections} = collectionState;
  const productId = product.get('_id');
  if ((product.get('sku-properties') || List()).size > 0) {
    const skuCollection = collections.find(isSkusCollection);
    return collectionState
      .itemsFor(skuCollection, locale ? {locale} : undefined)
      .filter((sku) => sku.get('product') === productId)
      .toList();
  }

  const defaultSku = getCMSItemFromStore(
    collectionState,
    product.get(DEFAULT_SKU_SLUG, '')
  );
  return defaultSku ? List([defaultSku]) : List();
};

export const getVariantsErrors = ({
  productItemState: {currentItemMeta, currentInlineItemMeta},
  validationErrorState,
}: {
  productItemState: Record<any, any>;
  validationErrorState: Record<any, any>;
}) => {
  const variants = currentInlineItemMeta.variants.size
    ? currentInlineItemMeta.variants
    : currentItemMeta.variants;

  // @ts-expect-error - TS7006 - Parameter 'variant' implicitly has an 'any' type.
  const variantIds = new Set(variants.valueSeq().map((variant) => variant._id));
  return (
    validationErrorState
      .getIn(['errors', 'variantItem'], Map())
      // @ts-expect-error - TS7006 - Parameter 'errs' implicitly has an 'any' type. | TS7006 - Parameter 'variantId' implicitly has an 'any' type.
      .flatMap((errs, variantId) => (variantIds.has(variantId) ? errs : Map()))
  );
};

export const cleanMultiImageFieldValues = (
  images: Array<ImageRefType | null>
) => {
  return images.filter(Boolean) as Array<ImageRefType>;
};

type KeyPath = Array<string | number>;
type KeyPathValue = {
  keyPath: KeyPath;
  value: any;
};
type KeyPathValues = KeyPathValue[];

export const objToKeyPathValues = (
  obj: Record<any, any>,
  keyPath: KeyPath = [],
  goDeeper: (arg1: KeyPathValue) => boolean = () => true
): KeyPathValues => {
  const keyPathValues: Array<KeyPathValue> = [];

  Object.entries(obj).forEach(([key, value]: [any, any]) => {
    const newKeyPath = [...keyPath, key];
    const keyPathValue = {keyPath: newKeyPath, value} as const;

    if (typeof value === 'object' && value != null && goDeeper(keyPathValue)) {
      keyPathValues.push(...objToKeyPathValues(value, newKeyPath, goDeeper));
    } else {
      keyPathValues.push(keyPathValue);
    }
  });

  return keyPathValues;
};

const commerceUtilPageSortOrder = [
  'checkout',
  'branch_checkout',
  'paypal-checkout',
  'branch_paypal-checkout',
  'order-confirmation',
  'branch_order-confirmation',
];

export const sortCommerceUtilPages = (
  lhs: PageRecordType,
  rhs: PageRecordType
) =>
  commerceUtilPageSortOrder.indexOf(lhs.get('slug')) -
  commerceUtilPageSortOrder.indexOf(rhs.get('slug'));

type IntlNumberFormatType = {
  fraction: string;
  decimal: string;
  group: string;
  currency: string;
};

export const generateIntlNumberFormat = (
  currency: string,
  locale: ValidCurrencyLocaleType,
  // number is 10000 because currencyAmountFormatOptions uses it in examples of amount format
  num: number = 10000
): IntlNumberFormatType =>
  // @ts-expect-error - TS2739 - Type 'Record<string, any>' is missing the following properties from type 'IntlNumberFormatType': fraction, decimal, group, currency
  new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
  })
    .formatToParts(num)
    .reduce<Record<string, any>>((formatItems, item) => {
      formatItems[item.type] = item.value;
      return formatItems;
    }, {});

export const getFractionDigits = (format: IntlNumberFormatType): number =>
  // if decimals exist for a Intl.NumberFormat they are depicted as a fraction with '0', '00', or '000'
  // covert this to number for fractionDigits (which is eventually stored in the defaultCurrencyFormat setting)
  format.fraction ? format.fraction.length : 0;

// Intl.NumberFormat.prototype.formatToParts() creates characters with different character codes
// in order for the pattern to match the code in one of the decimal options in `commerceConstants.js`
// we have to replace some of them
export const cleanFormatCharacters = (characters: string): string => {
  return characters
    .split('')
    .map((char) => {
      // if the character is a space (with Unicode Decimal Code #8239 or #160),
      // replace it with ' ' (which is Unicode Decimal Code #32)
      if ([160, 8239].indexOf(char.charCodeAt(0)) > -1) {
        return ' ';
      }
      // if the character is a comma (with Unicode Decimal Code #1643),
      // replace it with ',' (which is Unicode Decimal Code #44)
      if (char.charCodeAt(0) === 1643) {
        return ',';
      }
      // if the character is a back quote (with Unicode Decimal Code #8217),
      // replace it with '`' (which is Unicode Decimal Code #46)
      if (char.charCodeAt(0) === 8217) {
        return '`';
      }

      return char;
    })
    .join('');
};

export const cleanFormatPattern = (
  pattern: string
): ValidCurrencyFormatType => {
  const cleaned = cleanFormatCharacters(pattern);

  // Manually ensure that cleaned is a valid currency format type to make Flow happy
  switch (cleaned) {
    case ',.':
    case '.,':
    case '٬,':
    case '`.':
    case ' ,':
    case ' .':
    case ' $':
    case ',':
    case '.':
    case '`':
    case '٬':
    case ' ':
    case '': {
      return cleaned;
    }

    default: {
      return '';
    }
  }
};

// creates value for PriceTemplatePreview based on fractionDigits
// so preview reflects accurate number of decimals based on currency and locale
export const createPriceTemplatePreviewValue = (
  fractionDigits: number
): number => Number(`10000` + `0`.repeat(fractionDigits));

export const doItemsRequireShipping = (items: List<ItemRecordType>) =>
  items.some((item) => item.get('shippable') === true);

// takes in paymentGatewaySupport for a specific payment gateway and checks for truthy values
export const checkForPaymentGatewayConflict = (
  paymentGatewaySupport: PaymentGatewaySupportType
): boolean => {
  for (const [, value] of paymentGatewaySupport) {
    if (value === true) {
      return true;
    }
  }
  return false;
};

export const getWasBusinessAddressMint = ({
  businessAddress,
  savedBusinessAddress,
}: {
  businessAddress: Map<string, string>;
  savedBusinessAddress: Map<string, string>;
}): boolean => {
  // savedBusinessAddress starts with a default country so
  // we're going to rely on the existence of a city to determine
  // if the business address has been set before.
  return (
    !get(savedBusinessAddress, 'city') && Boolean(get(businessAddress, 'city'))
  );
};

export const getDiscountStatus = (discount: Discount): DiscountStatus => {
  if (!get(discount, 'active')) {
    return 'disabled';
  }

  const usage = get(discount, 'totalUsage', 0);
  const limit = getIn(discount, ['usage', 'limit', 'total'], 0);
  const isUnlimited = limit === 0;

  if (!isUnlimited && usage >= limit) {
    return 'limit-reached';
  }

  const nowMoment = moment();
  const expiresOnMoment = moment(get(discount, 'expiresOn'), moment.ISO_8601);

  if (
    !get(discount, 'neverExpires') &&
    expiresOnMoment.isSameOrBefore(nowMoment)
  ) {
    return 'expired';
  }

  const validOnMoment = moment(get(discount, 'validOn'), moment.ISO_8601);

  if (validOnMoment.isAfter(nowMoment)) {
    return 'scheduled';
  }

  return 'active';
};

const hasDefaultCommerceSettings = (
  settings: CommerceSettingsStateRecordShape
): boolean => settings.defaultCurrency && settings.defaultCurrencyLocale; // checks that commerce settings has defaults needed for ecommerce setup (i.e. `USD` and `en-US`)

export const checkIfEcommerceIsReady = (
  settings: CommerceSettingsStateRecordShape
): boolean =>
  // if ecommerce is enabled we want to make sure commerce settings have loaded
  settings.requestStatus === NETWORK_STATUSES.SUCCEEDED &&
  // and make sure the default commerce settings have been synced
  hasDefaultCommerceSettings(settings);

export const countTotalCommerceItems = (
  collections: CollectionsType,
  itemCountByCollectionId: ItemCountByCollectionIdType
): number => {
  return collections.reduce((total, collection) => {
    if (isCommerceCollection(collection)) {
      return total + itemCountByCollectionId.get(collection._id || '') || 0;
    }
    return total;
  }, 0);
};

// checks if `Required shipping` setting is being toggled off
export const isRequiredShippingBeingToggledOff = (
  keyPath: KeyPath,
  value: any
): boolean => keyPath[0] === 'shippable' && value === false;

// checks if `Track inventory` setting is being toggled off
export const isTrackInventoryBeingToggledOff = (
  keyPath: KeyPath,
  value: VariantValue | VariantsByOptionCombinationHash
): boolean =>
  (keyPath[2] === 'inventoryType' || keyPath[0] === 'product-inventory') &&
  value === INVENTORY_TYPE_INFINITE;

export const resetFieldValue = (
  value: any
): false | ListType<never> | MapType<never, never> | Array<never> | null => {
  if (typeof value === 'boolean') {
    return false;
  } else if (value && value.size > 0) {
    return value.clear();
  } else if (Array.isArray(value)) {
    return [];
  } else {
    return null;
  }
};

export const variantHasUploadingImages = (variant: Variant): boolean => {
  const mainImage = variant['main-image'];
  const moreImages = variant['more-images'];

  if (!mainImage && Array.isArray(moreImages) && moreImages.length === 0) {
    return false;
  }

  return (
    isFileUploading(mainImage) ||
    (moreImages && moreImages.some((img) => isFileUploading(img)))
  );
};

export const isOrderFulfilled = (
  order: Order | Map<string, Order[keyof Order]>
): boolean => {
  return get(order, 'status') === CLIENT_ORDER_STATES.FULFILLED;
};

export const openLink = (_path: string) => {
  // if the path doesn't start with http (and https by nature), then
  // we prepend // to the path so the window will treat it as an absolute URL
  // instead of a relative one.
  const path = !_path.startsWith('http') ? `//${_path}` : _path;

  global.open(path, '_blank');
};

export const hasAnyShippingRelatedFields = (
  order: Order | Map<string, Order[keyof Order]>
): boolean => {
  const provider = get(order, 'shippingProvider');
  const tracking = get(order, 'shippingTracking');
  const shippingTrackingURL = get(order, 'shippingTrackingURL');

  return provider || tracking || shippingTrackingURL;
};
